import { scrollIntoView } from '@theme/scrolling';
import { prefersReducedMotion } from '@theme/utilities';

class FaqTabs extends HTMLElement {
  #controller = new AbortController();

  /** @type {HTMLElement | null} */
  get #nav() {
    return this.querySelector('.faq-tabs__nav');
  }

  /** @type {HTMLButtonElement[]} */
  get #buttons() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-faq-tab-button]')]);
  }

  /** @type {HTMLElement[]} */
  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.#hoistButtons();

    const buttons = this.#buttons;
    if (buttons.length === 0) return;

    buttons.forEach((button) => {
      button.addEventListener('click', this.#handleClick, { signal });
      button.addEventListener('keydown', this.#handleKeyDown, { signal });
    });

    this.#activate(buttons[0], { focus: false, scroll: false });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #hoistButtons() {
    const nav = this.#nav;
    if (!nav) return;

    this.#buttons.forEach((button) => {
      if (button.parentElement === nav) return;
      nav.appendChild(button);
    });
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    this.#activate(button, { focus: false, scroll: true });
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const buttons = this.#buttons;
    const currentIndex = buttons.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    if (currentIndex === -1) return;

    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % buttons.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextButton = buttons[nextIndex];
    if (nextButton) this.#activate(nextButton, { focus: true, scroll: true });
  };

  /**
   * @param {HTMLButtonElement} activeButton
   * @param {{ focus: boolean, scroll: boolean }} options
   */
  #activate(activeButton, { focus, scroll }) {
    const targetPanelId = activeButton.getAttribute('aria-controls');

    this.#buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });

    this.#panels.forEach((panel) => {
      const isActive = panel.id === targetPanelId;
      panel.hidden = !isActive;
    });

    if (focus) activeButton.focus();

    if (scroll) this.#scrollButtonIntoNav(activeButton);
  }

  /** @param {HTMLButtonElement} activeButton */
  #scrollButtonIntoNav(activeButton) {
    const nav = this.#nav;
    if (!nav || nav.scrollWidth <= nav.clientWidth) return;

    scrollIntoView(activeButton, {
      ancestor: nav,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }
}

if (!customElements.get('faq-tabs')) {
  customElements.define('faq-tabs', FaqTabs);
}

class FaqTab extends HTMLElement {}

if (!customElements.get('faq-tab')) {
  customElements.define('faq-tab', FaqTab);
}
